import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnDestroy } from '@angular/core';

import { EventOption } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { flatMap, indexOf, sortBy } from 'lodash-es';
import { BehaviorSubject, noop } from 'rxjs';

import { TopEventsMappingService } from '../top-events-mapping.service';
import { TopEventsTrackingService } from '../top-events-tracking.service';
import { EventDataForOptionPanel, HandpickedOptionsModel, PodType, TopEventsPodData } from '../top-events.model';

@Component({
    selector: 'ms-top-events-props-handpickedoptionspod',
    templateUrl: 'top-events-props-handpickedoptionspod.component.html',
    styleUrl: 'top-events-props.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TopEventsPropsHandpickedOptionsComponent implements OnChanges, OnDestroy {
    @HostBinding('class') className = 'props-handpicked-options';

    @HostBinding('class.hidden-visibility') get columnsNotReady(): boolean {
        return !this.columns;
    }

    @Input() pod: TopEventsPodData;
    @Input() index: number;

    vms$ = new BehaviorSubject<HandpickedOptionsModel[]>([]);

    limit: number;
    vmCount: number;
    sameEvent = true;
    tracking = `TopEvents|${PodType.HandpickedOptionsPod}`;
    trackingModuleType = PodType.HandpickedOptionsPod;
    columns?: number;

    private showMoreClicked = false;
    isEpcotEnabled: boolean;

    constructor(
        public sitecore: Sitecore,
        private topEventsPodService: TopEventsMappingService,
        private topEventsTrackingService: TopEventsTrackingService,
        private changeDetectionRef: ChangeDetectorRef,
        private epcotConfig: EpcotConfig,
    ) {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    ngOnChanges(): void {
        this.pod.updateHandler = this.updateViewModel;

        if (this.pod.events?.length) {
            this.updateViewModel();
            this.limit = this.vmCount = this.vms$.value.length;
        }
    }

    private updateViewModel = (): void => {
        const vms = this.pod.events
            .map((e) =>
                this.mapToHandpickedVm(
                    this.topEventsPodService.getPodEventData({
                        eventData: e,
                        sourceFixtures: this.pod.sourceFixtures,
                        selectionTextOverride: this.pod.selectionTextOverride,
                        isDetailed: false,
                    }),
                    this.pod.previousOdds,
                ),
            )
            .flat();

        const eventOrder = this.pod.events.map((e) => e.id);

        const sorted = sortBy(
            vms,
            (vm) => indexOf(eventOrder, vm.eventModel.id),
            (vm) => indexOf([...this.pod.sourceFixtures[vm.eventModel.id].optionIds], vm.option.id),
        );

        this.sameEvent = this.pod.events?.length === 1;

        this.vms$.next(sorted);
    };

    private mapToHandpickedVm(vm: EventDataForOptionPanel, previousOdds?: NumberDictionary<NativePrice>): HandpickedOptionsModel[] {
        return flatMap(vm.preparedOptionGoups, (og) => {
            return flatMap(og.options, (o) => {
                return {
                    eventModel: vm.eventModel,
                    optionGroup: og,
                    option: o,
                    previousOdds: this.mapPreviousOdds(o, previousOdds?.[o.id]),
                } as HandpickedOptionsModel;
            });
        });
    }

    private mapPreviousOdds(option: EventOption, previousOdds?: NativePrice): NativePrice | undefined {
        if (!previousOdds) {
            return;
        }

        return previousOdds.odds < option.nativePrice.odds ? previousOdds : undefined;
    }

    trackItem(index: number, vm: HandpickedOptionsModel): number {
        return vm.option.id;
    }

    showMarkets(): void {
        this.showMoreClicked = true;
        this.limit = this.vms$.value.length;
        this.topEventsTrackingService.trackShowMoreLess(this.pod, this.index, false);
        this.changeDetectionRef.detectChanges();
    }

    calculateLimit(columns: number): void {
        if (!this.showMoreClicked) {
            this.limit = this.pod.optionsBeforeShowMore ? this.pod.optionsBeforeShowMore * columns : this.vms$.value.length;
        }
        this.columns = columns;
    }

    trackBottomLink(): void {
        this.topEventsTrackingService.trackBottomLink(this.pod, this.index);
    }

    hasNameOverride(id: number): boolean {
        return !!this.pod.selectionTextOverride[id];
    }

    hasDoubleRightBorder(index: number): boolean {
        return (index + 1) % this.columns! === 0;
    }

    hasDoubleBottomBorder(index: number): boolean {
        //if modulo result is 0 the full row has double border
        let optionsOnLastRow = this.columns!;
        if (this.vms$.value.length % this.columns! !== 0) {
            optionsOnLastRow = this.vms$.value.length % this.columns!;
        }

        return index >= this.vms$.value.length - optionsOnLastRow;
    }

    ngOnDestroy(): void {
        this.pod.updateHandler = noop;
    }
}
